<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { cn } from "@/lib/utils";
import { ArrowRight, Grip, X } from "lucide-vue-next";
import route from "ziggy-js";
import { Badge } from "@/components/ui/badge";
import { computed } from "vue";

const props = defineProps<{
    eventType: any;
    dragDisabled: boolean;
}>();

const emit = defineEmits(["toggleAttachment"]);

const isTeamEventType = computed(() => {
    if (typeof props.eventType?.isTeamEventType === "boolean") {
        return props.eventType.isTeamEventType;
    }

    return props.eventType?.team?.personal_team === false;
});

function goToEventTypeDetail(): void {
    router.visit(
        route("app.event-types.show", {
            eventType: props.eventType.id,
        }),
    );
}
</script>

<template>
    <div :class="cn('group relative h-full')">
        <button
            type="button"
            class="ring-iron-300 group rounded-md px-4 py-4 flex h-full w-full flex-col overflow-hidden ring-1"
            @click="goToEventTypeDetail"
        >
            <div class="inline-flex w-full items-center justify-between">
                <div class="min-w-0 gap-2 flex items-center text-left">
                    <span
                        class="text-sm font-medium text-iron-700 line-clamp-1 text-left"
                    >
                        {{ eventType.name }}
                    </span>

                    <Badge
                        v-if="isTeamEventType"
                        variant="outline"
                        class="border-z-primary-200 bg-z-primary-50 px-2 py-0 font-medium text-z-primary-600 text-[10px]"
                    >
                        {{ $t("event_types.create_event_type.team_event") }}
                    </Badge>
                </div>

                <div class="flex h-full items-start">
                    <ArrowRight
                        class="ml-2 h-5 w-5 text-iron-700 ease-out group-hover:translate-x-2 transition-all duration-150"
                    />
                </div>
            </div>

            <div class="mt-4 text-sm flex w-full grow flex-col text-left">
                <div
                    v-if="eventType.configuration?.locale?.texts?.introduction"
                    class="text-iron-600 [&_p]:m-0 line-clamp-3 flex-1"
                    v-html="
                        eventType.configuration?.locale?.texts?.introduction
                    "
                />
                <div v-else class="flex-1" />

                <div
                    v-if="eventType.duration"
                    class="bg-iron-100 mt-3 rounded-md px-1.5 py-1 text-xs ring-iron-200 inline-flex self-start ring-1"
                >
                    {{ eventType.duration }} min
                </div>
            </div>
        </button>

        <button
            type="button"
            class="-right-2 -top-2 bg-white p-1 text-iron-400 shadow ring-iron-200 hover:text-iron-700 absolute rounded-full opacity-0 ring-1 transition group-hover:opacity-100"
            :aria-label="$t('remove')"
            @click.stop="emit('toggleAttachment', props.eventType)"
        >
            <X class="h-3.5 w-3.5" />
        </button>

        <div
            v-if="!dragDisabled"
            class="event-card-handle -left-2 -top-2 bg-white p-1 text-iron-400 shadow ring-iron-200 absolute cursor-move rounded-full opacity-0 ring-1 transition group-hover:opacity-100"
        >
            <Grip class="h-3.5 w-3.5" />
        </div>
    </div>
</template>
